import LiquidIridescentBackground from "@/components/background/LiquidIridescentBackground";
import WebGLIridescentBackground from "@/components/background/WebGLIridescentBackground";
import WebGLAuroraBorealisBackground from "@/components/background/WebGLAuroraBorealisBackground";
import WebGLMeshGradientBackground from '@/components/background/WebGLMeshGradientBackground';
import WebGLLiquidMarbleBackground from "@/components/background/WebGLLiquidMarbleBackground";
import WebGLPortalBackground from "@/components/background/WebGLPortalBackground";
import WebGLPoolBackground from "@/components/background/WebGLPoolBackground";
import WebGLCyberfluidBackground from "@/components/background/WebGLCyberfluidBackground";
import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { UnderMaintenancePage } from "@/components/public/UnderMaintenancePage";
import { supabase } from "@/integrations/backend/client";
import { Link as LinkType, PageSettings, Profile } from "@/types/editor";
import { getButtonClass } from "@/lib/linkStyle";
import { motion } from "framer-motion";
import { ExternalLink, Loader2, ShoppingBag, Bot, icons, Eye, Share2, Mail } from "lucide-react";
import { processLinksWithABTesting } from "@/lib/abTesting";
import { getBackgroundStyle, getPresetAnimationClass, getVideoOverlayStyle } from "@/lib/background";
import VideoBackground from "@/components/background/VideoBackground";
import { getLinkButtonStyle, getGlobalButtonStyle, resolveButtonColors } from '@/lib/linkStyle';
import { shouldShowStoreLink } from '@/lib/storeVisibility';
import { shouldShowContactLink } from '@/lib/contactVisibility';
import { mergeWithPositionedVirtuals } from '@/lib/virtualLinkMerge';
import { getContactBucket, getStoreBucket } from '@/lib/virtualLinkBuckets';
import { setBioTheme, getBioTheme, getInstantBg } from '@/lib/bioThemeCache';
import { SeoHead } from "@/components/seo/SeoHead";
import { useLanguage } from "@/contexts/LanguageContext";
import { pickByLanguage } from "@/lib/language";
import { getSafeHref } from "@/lib/socialRedirect";
import { ProfileAvatar } from "@/components/ui/ProfileAvatar";
import { ChatWidget } from '@/components/chat/ChatWidget';

interface Subscription {
  plan_id: string;
}

export default function PublicBioPage() {
  const { language } = useLanguage();
  const t = (pt: string, en: string) => pickByLanguage(language, { pt, en });
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const previewToken = searchParams.get('preview');
  
  const [profile, setProfile] = useState<Profile | null>(null);
  const [links, setLinks] = useState<LinkType[]>([]);
  const [settings, setSettings] = useState<PageSettings | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [hasStoreProducts, setHasStoreProducts] = useState(false);
  const [showContactLink, setShowContactLink] = useState(false);
  const [chatAgentId, setChatAgentId] = useState<string | null>(null);
  const [chatButtonText, setChatButtonText] = useState('Fale com meu Assistente');
  const [chatDisplayMode, setChatDisplayMode] = useState<'button' | 'icon' | 'widget'>('button');
  /** Position of the chatbot button within the links list (null = end of list / legacy). */
  const [chatBioLinkPosition, setChatBioLinkPosition] = useState<number | null>(null);
  const [chatButtonStyle, setChatButtonStyle] = useState<Pick<LinkType, 'button_color' | 'button_text_color' | 'button_gradient_type' | 'button_gradient_end' | 'button_gradient_angle' | 'use_global_theme'>>({
    button_color: null,
    button_text_color: null,
    button_gradient_type: 'solid',
    button_gradient_end: null,
    button_gradient_angle: 135,
    use_global_theme: true,
  });
  const [chatWidgetIcon, setChatWidgetIcon] = useState<string | null>(null);
  const [chatWidgetIconUrl, setChatWidgetIconUrl] = useState<string | null>(null);
  const [chatWidgetColor, setChatWidgetColor] = useState<string | null>(null);
  const [chatWidgetTextColor, setChatWidgetTextColor] = useState<string | null>(null);
  const [chatAvatarUrl, setChatAvatarUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [viewSource, setViewSource] = useState<'public' | 'preview' | 'owner'>('public');
  // Only track analytics when the bio owner has a paid plan AND the visit is genuinely public
  const [canTrackAnalytics, setCanTrackAnalytics] = useState(false);

  const normalizeChatDisplayMode = (value: string | null | undefined): 'button' | 'icon' | 'widget' => {
    if (value === 'icon' || value === 'widget') return value;
    return 'button';
  };

  const renderAlignedButtonContent = (
    label: string,
    leadingIcon?: React.ReactNode | null,
    trailingIcon?: React.ReactNode | null,
    className = "py-[1.05rem] px-4 sm:py-4 sm:px-6",
  ) => (
    <div className={`flex w-full min-w-0 items-center ${className}`}>
      <div className="flex w-6 shrink-0 items-center justify-start">
        {leadingIcon ?? null}
      </div>
      <span className="flex-1 min-w-0 truncate text-center font-medium text-[15px] sm:text-base" title={label}>
        {label}
      </span>
      <div className="flex w-6 shrink-0 items-center justify-center">
        {trailingIcon ?? null}
      </div>
    </div>
  );

  const renderChatTriggerVisual = () => {
    if (chatWidgetIconUrl) {
      return <img src={chatWidgetIconUrl} alt={chatButtonText} className="h-9 w-9 rounded-full object-cover" />;
    }
    if (chatWidgetIcon) {
      return <span className="text-xl leading-none select-none">{chatWidgetIcon}</span>;
    }
    if (chatAvatarUrl) {
      return <img src={chatAvatarUrl} alt={chatButtonText} className="h-9 w-9 rounded-full object-cover" />;
    }
    return <Bot className="h-5 w-5" style={{ color: chatWidgetTextColor ?? undefined }} />;
  };

  useEffect(() => {
    async function fetchData() {
      if (!username) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        let publicProfile = null;
        let userId: string | null = null;
        let detectedViewSource: 'public' | 'preview' | 'owner' = 'public';

        // Check if accessing via preview token
        if (previewToken) {
          // Validate token and get user_id
          const { data: tokenUserId, error: tokenError } = await supabase.rpc('validate_preview_token', {
            _token: previewToken
          });

          if (!tokenError && tokenUserId) {
            userId = tokenUserId as string;
            
            // Fetch profile via preview token
            const { data: tokenProfile } = await supabase
              .from("profiles")
              .select("*")
              .eq("user_id", userId)
              .eq("username", username)
              .maybeSingle();

            if (tokenProfile) {
              publicProfile = tokenProfile;
              setIsPreview(true);
              detectedViewSource = 'preview';
            }
          }
        }

        // If no token or token invalid, try published profile
        if (!publicProfile) {
          const { data, error } = await supabase
            .from("profiles_public")
            .select("*")
            .eq("username", username)
            .maybeSingle();
          
          publicProfile = data;

          // If not found in public view, check if current user is the owner
          if (!publicProfile) {
            const { data: { user } } = await supabase.auth.getUser();
            
            if (user) {
              // Check if this user owns this username
              const { data: ownerProfile } = await supabase
                .from("profiles")
                .select("*")
                .eq("username", username)
                .eq("user_id", user.id)
                .maybeSingle();
              
              if (ownerProfile) {
                // User is viewing their own unpublished page
                publicProfile = ownerProfile;
                userId = user.id;
                setIsPreview(true);
                detectedViewSource = 'owner';
              }
            }
          }
        }

        if (!publicProfile) {
          // Check if the username was renamed — resolve redirect.
          const { data: redirectRow } = await (supabase
            .from('username_redirects' as any)
            .select('user_id')
            .eq('old_username', username)
            .maybeSingle() as any);
          if (redirectRow?.user_id) {
            const { data: currentProfiles } = await (supabase
              .from('profiles' as any)
              .select('username')
              .eq('user_id', redirectRow.user_id as string)
              .eq('is_published', true)
              .maybeSingle() as any);
            const currentProfile = currentProfiles as { username?: string } | null;
            if (currentProfile?.username) {
              navigate(`/${currentProfile.username}`, { replace: true });
              return;
            }
          }

          // Check if username exists but page is unpublished (maintenance mode)
          const { data: statusRows } = await (supabase.rpc as any)('get_page_status', { p_username: username });
          const status = statusRows?.[0];
          if (status?.page_exists && !status.is_published) {
            setIsUnderMaintenance(true);
          } else {
            setNotFound(true);
          }
          setLoading(false);
          return;
        }

        // Get user_id if not already set
        if (!userId) {
          const { data: userIdData } = await supabase.rpc("get_user_id_by_username", { _username: username });
          userId = userIdData as string | null;
        }

        if (!userId) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setViewSource(detectedViewSource);

        // Map public profile to Profile type
        setProfile({
          id: publicProfile.id,
          user_id: userId,
          username: publicProfile.username,
          full_name: publicProfile.full_name ?? null,
          display_name: publicProfile.display_name,
          bio: publicProfile.bio,
          avatar_url: publicProfile.avatar_url,
          is_published: publicProfile.is_published ?? true,
          created_at: publicProfile.created_at,
          updated_at: publicProfile.updated_at || publicProfile.created_at,
        } as Profile);

        // Fetch subscription plan using secure view (only exposes plan_id)
        const { data: subData } = await supabase
          .from("user_plans")
          .select("plan_id")
          .eq("user_id", userId)
          .maybeSingle();

        if (subData) {
          setSubscription(subData as Subscription);
        }

        const ownerPlanId = (subData?.plan_id as string) || 'free';
        const hasActivePaidPlan = ownerPlanId !== 'free';

        // Track analytics only for genuine public visits on paid plans
        const trackable = hasActivePaidPlan && detectedViewSource === 'public';
        setCanTrackAnalytics(trackable);

        // Track page view only if bio owner has an active paid plan and it's a real public visit
        if (trackable) {
          await supabase.from("page_views").insert({
            user_id: userId,
            view_source: detectedViewSource,
            referrer: document.referrer || null,
            user_agent: navigator.userAgent || null,
          });
        }

        // Fetch links
        const { data: linksData } = await supabase
          .from("links")
          .select("*")
          .eq("user_id", userId)
          .eq("is_active", true)
          .order("position", { ascending: true });

        // Filter links based on scheduling
        const now = new Date();
        const visibleLinks = (linksData as LinkType[] || []).filter(link => {
          // If scheduled to publish in the future, don't show yet
          if (link.scheduled_publish_at) {
            const publishDate = new Date(link.scheduled_publish_at);
            if (publishDate > now) {
              return false;
            }
          }
          
          // If scheduled to expire in the past, don't show
          if (link.scheduled_expire_at) {
            const expireDate = new Date(link.scheduled_expire_at);
            if (expireDate <= now) {
              return false;
            }
          }
          
          return true;
        });

        // Process A/B testing variants
        const processedLinks = await processLinksWithABTesting(visibleLinks, userId);
        setLinks(processedLinks);

        // Fetch settings
        const { data: settingsData } = await supabase
          .from("page_settings")
          .select("*")
          .eq("user_id", userId)
          .maybeSingle();

        if (settingsData) {
          setSettings(settingsData as unknown as PageSettings);
          // Cache for instant themed transitions to sub-pages
          if (publicProfile) {
            setBioTheme(publicProfile.username, {
              settings: settingsData as unknown as PageSettings,
              profile: {
                username: publicProfile.username,
                display_name: publicProfile.display_name ?? null,
                bio: publicProfile.bio ?? null,
                avatar_url: publicProfile.avatar_url ?? null,
              },
            });
          }
        }

        // Check if store link is enabled, user has Pro+ plan, and at least one active product exists
        const userPlanId = (subData?.plan_id as string) || 'free';
        const storeEnabled = settingsData?.store_link_enabled === true;
        if (storeEnabled) {
          const { data: activeProducts } = await supabase
            .from("products")
            .select("id")
            .eq("user_id", userId)
            .eq("is_active", true)
            .limit(1);
          setHasStoreProducts(
            shouldShowStoreLink({
              storeLinkEnabled: storeEnabled,
              activeProductsCount: activeProducts?.length || 0,
              planId: userPlanId as any,
            }),
          );
        } else {
          setHasStoreProducts(false);
        }

        // Check if contact link should be shown (plan-gated + enabled)
        setShowContactLink(
          shouldShowContactLink({
            emailCollectionEnabled: settingsData?.email_collection_enabled,
            planId: userPlanId as any,
          }),
        );

        // Show chat on the public bio whenever there is an active agent configured
        // to appear in the bio. Feature gating already happens on the dashboard side.
        if (userId) {
          const { data: agentData } = await supabase
            .from('chat_agents')
            .select('id, bio_button_text, bio_display_mode, bio_link_position, bio_button_use_global_theme, bio_button_color, bio_button_text_color, bio_button_gradient_type, bio_button_gradient_end, bio_button_gradient_angle, widget_icon, widget_icon_url, widget_color, widget_text_color, avatar_url, system_prompt')
            .eq('user_id', userId)
            .eq('is_enabled', true)
            .eq('show_in_bio', true)
            .order('updated_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          // Agent must have a non-empty system_prompt to be considered valid.
          // Mirrors chatReady check in ChatAgentConfigPanel.
          const agentIsValid = agentData && (agentData as any).system_prompt?.trim().length > 0;
          if (agentIsValid && agentData) {
            const ad = agentData as any;
            setChatAgentId(ad.id);
            setChatButtonText(ad.bio_button_text || 'Fale com meu Assistente');
            setChatDisplayMode(normalizeChatDisplayMode(ad.bio_display_mode));
            setChatBioLinkPosition(ad.bio_link_position ?? null);
            setChatButtonStyle({
              button_color: ad.bio_button_color ?? null,
              button_text_color: ad.bio_button_text_color ?? null,
              button_gradient_type: (ad.bio_button_gradient_type ?? 'solid') as 'solid' | 'linear' | 'radial' | 'conic',
              button_gradient_end: ad.bio_button_gradient_end ?? null,
              button_gradient_angle: ad.bio_button_gradient_angle ?? 135,
              use_global_theme: ad.bio_button_use_global_theme ?? true,
            });
            setChatWidgetIcon(ad.widget_icon ?? null);
            setChatWidgetIconUrl(ad.widget_icon_url ?? null);
            setChatWidgetColor(ad.widget_color ?? null);
            setChatWidgetTextColor(ad.widget_text_color ?? null);
            setChatAvatarUrl(ad.avatar_url ?? null);
          } else {
            setChatAgentId(null);
            setChatButtonText('Fale com meu Assistente');
            setChatDisplayMode('button');
            setChatBioLinkPosition(null);
            setChatButtonStyle({
              button_color: null,
              button_text_color: null,
              button_gradient_type: 'solid',
              button_gradient_end: null,
              button_gradient_angle: 135,
              use_global_theme: true,
            });
            setChatWidgetIcon(null);
            setChatWidgetIconUrl(null);
            setChatWidgetColor(null);
            setChatWidgetTextColor(null);
            setChatAvatarUrl(null);
          }
        } else {
          setChatAgentId(null);
          setChatButtonText('Fale com meu Assistente');
          setChatDisplayMode('button');
          setChatBioLinkPosition(null);
          setChatButtonStyle({
            button_color: null,
            button_text_color: null,
            button_gradient_type: 'solid',
            button_gradient_end: null,
            button_gradient_angle: 135,
            use_global_theme: true,
          });
          setChatWidgetIcon(null);
          setChatWidgetIconUrl(null);
          setChatWidgetColor(null);
          setChatWidgetTextColor(null);
          setChatAvatarUrl(null);
        }
      } catch (error) {
        console.error("Error fetching bio page:", error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [username, previewToken]);

  // Social-media links open directly because in-app browsers are fragile when
  // an intermediate redirect is involved. The anchor itself strips referrer
  // data via rel="noreferrer" + referrerPolicy="no-referrer".

  if (loading) {
    const cached = getBioTheme(username ?? '');
    const bgStyle = cached ? getInstantBg(cached.settings) : {};
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0.12 } }}
        transition={{ duration: 0.22, ease: 'easeOut', delay: 0.15 }}
        className="min-h-screen flex items-center justify-center"
        style={cached ? { ...bgStyle } : { background: 'hsl(var(--background))' }}
      >
        <Loader2 className="h-8 w-8 animate-spin motion-reduce:animate-none" style={cached ? { color: cached.settings.text_color } : {}} />
      </motion.div>
    );
  }

  if (isUnderMaintenance) {
    return <UnderMaintenancePage username={username} />;
  }

  if (notFound || !settings) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-8">
        <h1 className="text-2xl font-bold text-foreground mb-4">{t("Página não encontrada", "Page not found")}</h1>
        <p className="text-muted-foreground mb-8">{t("Esta bio page não existe ou ainda não foi configurada.", "This bio page doesn't exist or hasn't been set up yet.")}</p>
        <Link to="/" className="text-primary hover:underline">
          {t("Crie sua bio page →", "Create your own bio page →")}
        </Link>
      </div>
    );
  }

  const buttonClass = getButtonClass(settings);
  const { btnBg, btnText } = resolveButtonColors(settings);

  const shouldNoIndex = isPreview || profile?.is_published === false;
  const contactPath = previewToken
    ? `/${username}/contact?preview=${encodeURIComponent(previewToken)}`
    : `/${username}/contact`;
  const storePath = previewToken
    ? `/${username}/store?preview=${encodeURIComponent(previewToken)}`
    : `/${username}/store`;
  const pageTitle = profile?.display_name
    ? `${profile.display_name} | Qlivar`
    : `@${username} | Qlivar`;
  const pageDescription = profile?.bio
    ? profile.bio
    : `Veja os links de ${profile?.display_name || `@${username}`} no Qlivar.`;

  // ── Position-aware link list ───────────────────────────────────────────────
  // Virtual buttons (chat, contact) may have an explicit position within the
  // links list (set via DnD in the editor).  If they do, they're rendered at
  // that slot.  If not (null), the old behaviour applies (chat → bottom of
  // list; contact → legacy top/bottom determined by email_collection_position).
  type MergedLinkItem =
    | { kind: 'link'; link: LinkType; index: number }
    | { kind: 'chat' }
    | { kind: 'contact' }
    | { kind: 'store' };

  const mergedLinkItems: MergedLinkItem[] = (() => {
    const positionedVirtuals: Array<{ item: MergedLinkItem; position: number }> = [];
    const trailingVirtuals: MergedLinkItem[] = [];

    const chatPos = chatAgentId && chatDisplayMode === 'button' ? chatBioLinkPosition : null;
    if (chatAgentId && chatDisplayMode === 'button') {
      if (chatPos != null && chatPos >= 0) {
        positionedVirtuals.push({ item: { kind: 'chat' }, position: chatPos });
      } else {
        trailingVirtuals.push({ kind: 'chat' });
      }
    }

    if (showContactLink && settings && settings.email_collection_display_mode !== 'icon') {
      const contactBucket = getContactBucket({
        contactLinkPosition: settings.contact_link_position,
        emailCollectionPosition: settings.email_collection_position,
      });
      if (contactBucket === 'positioned') {
        positionedVirtuals.push({ item: { kind: 'contact' }, position: settings.contact_link_position! });
      } else if (contactBucket === 'trailing') {
        trailingVirtuals.push({ kind: 'contact' });
      }
      // 'top-only' is rendered in a separate JSX section above the list
    }

    if (hasStoreProducts && settings) {
      const storeBucket = getStoreBucket({ storeLinkPosition: settings.store_link_position });
      if (storeBucket === 'positioned') {
        positionedVirtuals.push({ item: { kind: 'store' }, position: settings.store_link_position! });
      } else {
        trailingVirtuals.push({ kind: 'store' });
      }
    }

    const realItems = links.map<MergedLinkItem>(
      (link, index) => ({ kind: 'link' as const, link, index }),
    );
    return mergeWithPositionedVirtuals(realItems, positionedVirtuals, trailingVirtuals);
  })();
  // ── End position-aware link list ───────────────────────────────────────────

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.12 } }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className={`min-h-screen relative ${getPresetAnimationClass(settings) ?? ''}`}
      style={{
        background: getPresetAnimationClass(settings)
          ? undefined
          : (settings.background_mode === 'custom' && settings.background_video_url)
            ? settings.background_color
            : getBackgroundStyle(settings),
        fontFamily: settings.font_family,
      }}
    >
      {getPresetAnimationClass(settings) === 'liquid-iridescent-bg'    && <LiquidIridescentBackground key={getPresetAnimationClass(settings)} />}
      {getPresetAnimationClass(settings) === 'webgl-iridescent-bg'      && <WebGLIridescentBackground key={getPresetAnimationClass(settings)} />}
      {getPresetAnimationClass(settings) === 'webgl-aurora-borealis-bg' && <WebGLAuroraBorealisBackground key={getPresetAnimationClass(settings)} />}
      {getPresetAnimationClass(settings) === 'webgl-portal' && <WebGLPortalBackground key={getPresetAnimationClass(settings)} />}
      {getPresetAnimationClass(settings) === 'webgl-pool' && <WebGLPoolBackground key={getPresetAnimationClass(settings)} />}
      {getPresetAnimationClass(settings) === 'webgl-cyber-fluid' && <WebGLCyberfluidBackground key={getPresetAnimationClass(settings)} />}
      {getPresetAnimationClass(settings)?.startsWith('webgl-liquid-') && <WebGLLiquidMarbleBackground key={getPresetAnimationClass(settings)} variant={getPresetAnimationClass(settings)?.replace('webgl-liquid-', '')} />}
      {getPresetAnimationClass(settings)?.startsWith('webgl-mesh-')   && <WebGLMeshGradientBackground key={getPresetAnimationClass(settings)} variant={getPresetAnimationClass(settings)?.replace('webgl-mesh-', '') as any} />}
      {/* Video background */}
      {settings.background_mode === 'custom' && settings.background_video_url && (
        <>
          <VideoBackground
            url={settings.background_video_url}
            loop={settings.background_video_loop ?? true}
            fit={settings.background_video_fit ?? 'cover'}
            showSoundToggle={!(settings.background_video_muted ?? true)}
            accentColor={settings.text_color ?? undefined}
          />
          {settings.background_overlay_enabled && (
            <div
              className="absolute inset-0"
              style={{ background: getVideoOverlayStyle(settings) ?? undefined, zIndex: 2 }}
            />
          )}
        </>
      )}
      <div className="relative z-10">
      <SeoHead
        title={pageTitle}
        description={pageDescription}
        path={`/${username}`}
        type="profile"
        noindex={shouldNoIndex}
      />
      {/* Import Google Fonts dynamically */}
      <link
        href={`https://fonts.googleapis.com/css2?family=${settings.font_family.replace(" ", "+")}:wght@400;500;600;700&display=swap`}
        rel="stylesheet"
      />

      {/* Preview Mode Banner */}
      {isPreview && (
        <div className="bg-yellow-500 text-black px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2">
          {viewSource === 'preview' ? (
            <>
              <Share2 className="inline-block h-4 w-4" />
              {t("Prévia compartilhada - este é um link temporário", "Shared Preview - This is a temporary preview link")}
            </>
          ) : (
            <>
              <Eye className="inline-block h-4 w-4" />
              {t("Modo de prévia - esta página ainda não está publicada. Só você pode vê-la.", "Preview Mode - This page is not published yet. Only you can see it.")}
            </>
          )}
        </div>
      )}

      <div className="max-w-md mx-auto px-5 sm:px-6 pt-[max(2.5rem,env(safe-area-inset-top))] pb-[max(4rem,calc(env(safe-area-inset-bottom)+1.5rem))]">
        {/* Avatar & Name */}
        {settings.show_avatar && (
          <motion.div
            className="flex flex-col items-center mb-9 sm:mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
              className="mb-5"
            >
              <ProfileAvatar
                shape={settings.avatar_shape ?? 'circle'}
                frame={settings.avatar_frame ?? 'none'}
                frameColor={settings.avatar_frame_color}
                avatarUrl={profile?.avatar_url}
                fallback={profile?.display_name?.[0]?.toUpperCase() || username?.[0]?.toUpperCase() || '?'}
                btnBg={btnBg}
                btnText={btnText}
              />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.18, ease: 'easeOut' }}
              className="text-[1.7rem] sm:text-[1.85rem] font-bold tracking-tight leading-[1.15] text-center text-balance"
              style={{ color: settings.text_color }}
            >
              {profile?.display_name || `@${username}`}
            </motion.h1>
            {profile?.display_name && username && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.55 }}
                transition={{ duration: 0.4, delay: 0.24 }}
                className="mt-1 text-[0.8rem] font-medium tracking-wide"
                style={{ color: settings.text_color }}
              >
                @{username}
              </motion.p>
            )}
            {settings.show_bio && profile?.bio && (
              <motion.p
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 0.92, y: 0 }}
                transition={{ duration: 0.4, delay: 0.28 }}
                className="text-center mt-3 max-w-[20rem] text-[0.95rem] leading-relaxed text-balance"
                style={{ color: settings.text_color }}
              >
                {profile.bio}
              </motion.p>
            )}

            {/* Icon row — chat and/or contact in icon display mode */}
            {((chatAgentId && chatDisplayMode === 'icon') || (showContactLink && settings?.email_collection_display_mode === 'icon')) && (
              <motion.div
                className="flex items-center justify-center gap-3 mt-5"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.32 }}
              >
                {chatAgentId && chatDisplayMode === 'icon' && (
                  <Link
                    to={`/chat/${chatAgentId}`}
                    title={chatButtonText}
                    className="group relative"
                  >
                    <div
                      className="h-12 w-12 rounded-full flex items-center justify-center shadow-lg transition-[box-shadow,opacity] duration-200 group-hover:shadow-xl overflow-hidden ring-1 ring-black/5"
                      style={{
                        background: chatWidgetColor || getLinkButtonStyle(chatButtonStyle, settings).background,
                        color: chatWidgetTextColor || getLinkButtonStyle(chatButtonStyle, settings).color,
                      }}
                    >
                      {renderChatTriggerVisual()}
                    </div>
                    <span className="sr-only">{chatButtonText}</span>
                  </Link>
                )}
                {showContactLink && settings?.email_collection_display_mode === 'icon' && (
                  <Link
                    to={contactPath}
                    title={settings.email_collection_button_text || t('Fale comigo', 'Contact Me')}
                    className="group relative"
                  >
                    <div
                      className={`h-12 w-12 rounded-full flex items-center justify-center shadow-lg transition-[box-shadow,opacity] duration-200 group-hover:shadow-xl ring-1 ring-black/5 ${buttonClass}`}
                      style={getGlobalButtonStyle(settings)}
                    >
                      <Mail className="h-5 w-5" />
                    </div>
                    <span className="sr-only">{settings.email_collection_button_text || t('Fale comigo', 'Contact Me')}</span>
                  </Link>
                )}
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Contact Link - Top Position (button mode only, legacy behaviour when no explicit position is stored) */}
        {showContactLink && settings && profile && settings.email_collection_display_mode !== 'icon' && settings.contact_link_position == null && (
          settings.email_collection_position === 'top'
        ) && (
          <motion.div
            className="mb-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Link
              to={contactPath}
              className={`group block w-full overflow-hidden ${buttonClass} transition-[box-shadow,opacity] duration-200 hover:shadow-lg`}
              style={getGlobalButtonStyle(settings)}
            >
              {renderAlignedButtonContent(
                settings.email_collection_button_text || t("Fale comigo", "Contact Me"),
                <Mail className="h-4 w-4 flex-shrink-0" />,
              )}
            </Link>
          </motion.div>
        )}

        {/* Links + virtual buttons (chatbot / contact form) — position-aware */}
        <div className="space-y-3.5 sm:space-y-3">
          {mergedLinkItems.map((item, idx) => {
            const stagger = Math.min(idx, 10) * 0.06;
            // ── Chat bot button ────────────────────────────────────────────
            if (item.kind === 'chat') {
              return (
                <motion.div
                  key="__chat__"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.42, delay: stagger, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={`/chat/${chatAgentId}`}
                    className={`group block w-full overflow-hidden font-medium transition-[box-shadow,opacity] duration-200 hover:shadow-lg ${buttonClass}`}
                    style={getLinkButtonStyle(chatButtonStyle, settings)}
                  >
                    {renderAlignedButtonContent(
                      chatButtonText,
                      <Bot className="h-4 w-4 flex-shrink-0" />,
                    )}
                  </Link>
                </motion.div>
              );
            }

            // ── Contact form button ────────────────────────────────────────
            if (item.kind === 'contact') {
              return (
                <motion.div
                  key="__contact__"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.42, delay: stagger, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={contactPath}
                    className={`group block w-full overflow-hidden ${buttonClass} transition-[box-shadow,opacity] duration-200 hover:shadow-lg`}
                    style={getGlobalButtonStyle(settings)}
                  >
                    {renderAlignedButtonContent(
                      settings.email_collection_button_text || t("Fale comigo", "Contact Me"),
                      <Mail className="h-4 w-4 flex-shrink-0" />,
                    )}
                  </Link>
                </motion.div>
              );
            }

            // ── Store button ───────────────────────────────────────────────
            if (item.kind === 'store') {
              return (
                <motion.div
                  key="__store__"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.42, delay: stagger, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={storePath}
                    className={`group block w-full overflow-hidden transition-[box-shadow,opacity] duration-200 hover:shadow-lg ${buttonClass}`}
                    style={getGlobalButtonStyle(settings)}
                  >
                    {renderAlignedButtonContent(
                      settings.store_button_text || t("Compre agora", "Shop Now"),
                      <ShoppingBag className="h-4 w-4 flex-shrink-0" />,
                    )}
                  </Link>
                </motion.div>
              );
            }

            // ── Regular link ───────────────────────────────────────────────
            const { link, index } = item;
            const hasImage = !!link.image_url;
            const imagePosition = link.image_position || "top";
            const isHorizontal = imagePosition === "left" || imagePosition === "right";

            return (
              <motion.div
                key={link.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.42, delay: Math.min(index, 10) * 0.06, ease: [0.16, 1, 0.3, 1] }}
              >
                <a
                  href={getSafeHref(link.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  referrerPolicy="no-referrer"
                  className={`group block w-full overflow-hidden font-medium transition-[box-shadow,opacity] duration-200 hover:shadow-lg ${buttonClass}`}
                  style={getLinkButtonStyle(link, settings)}
                  onClick={() => {
                    if (canTrackAnalytics && profile?.user_id) {
                      supabase.from('link_clicks').insert({
                        link_id: link.id,
                        user_id: profile.user_id,
                        referrer: document.referrer || null,
                        user_agent: navigator.userAgent || null,
                      }).then();
                    }
                  }}
                >
                  <div
                    className={`flex ${
                      isHorizontal
                        ? imagePosition === "left"
                          ? "flex-row"
                          : "flex-row-reverse"
                        : imagePosition === "top"
                          ? "flex-col"
                          : "flex-col-reverse"
                    }`}
                  >
                    {hasImage && (
                      <div className={`flex-shrink-0 overflow-hidden ${isHorizontal ? "w-16 h-16" : "w-full h-28"}`}>
                        <img
                          src={link.image_url!}
                          alt=""
                          className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                          onError={(e) => { (e.currentTarget.parentElement as HTMLElement).style.display = 'none'; }}
                        />
                      </div>
                    )}
                    {renderAlignedButtonContent(
                      link.title,
                      (() => {
                        if (link.icon) {
                          const iconName = link.icon.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join('') as keyof typeof icons;
                          const IconComp = icons[iconName];
                          if (IconComp) return <IconComp className="h-4 w-4 flex-shrink-0" />;
                        }
                        return null;
                      })(),
                      <ExternalLink className="h-4 w-4 opacity-50 transition-opacity duration-200 group-hover:opacity-90 flex-shrink-0" />,
                      hasImage && isHorizontal ? "py-3.5 px-3 flex-1 min-w-0 sm:py-3" : hasImage ? "py-3.5 px-4 sm:py-3" : "py-[1.05rem] px-4 sm:py-4 sm:px-6",
                    )}
                  </div>
                </a>
              </motion.div>
            );
          })}
        </div>

        {/* Discrete footer for all users */}
        {!settings.hide_branding && (
        <motion.div
          className="mt-14 pb-6 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <Link
            to="/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[11px] tracking-wide uppercase opacity-40 hover:opacity-80 transition-opacity"
            style={{ color: settings.text_color }}
          >
            <span>{t("Feito com", "Made with")}</span>
            <img alt="Qlivar" className="h-3 w-3" src="/uploads/c60b833a-df7a-4878-bcf0-647598b01880.svg" />
            <span className="font-semibold">Qlivar</span>
          </Link>
        </motion.div>
        )}
      </div>
      </div>
      {/* Floating chat widget (widget display mode) */}
      {chatAgentId && chatDisplayMode === 'widget' && (
        <ChatWidget agentId={chatAgentId} pageOwnerId={profile?.user_id} settings={settings} />
      )}
    </motion.div>
  );
}
